import React from 'react';
import { BasicPageWrapper, GivelifyLabel, GivelifyLink } from '@givelify/ui';
import { pushEvent } from '@givelify/utils';
import { Stack } from '@mui/material';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useConnectorState } from '../../../provider/Connector';
import { GiveNowButtonTypes } from '../../../types';
import { I18N_NAMESPACE } from '../../../types/const';
import { ONLINE_GIVING_LANDED } from '../../../utils/cleverTapEvents';
import { useTranslations } from '../../../utils/translations';
import useCustomModal from '../Modals/useCustomModal';
import useInstructModal from '../Modals/useInstructModal';
import { useShareModal } from '../Modals/useShareModal';
import { ButtonPreview } from './ButtonPreview';
import { Breadcrumbs, Container, SectionDivider } from './style';

export const GivelifyButtonPage: React.FCC = () => {
    const navigate = useNavigate();
    const { givelifyButton: gb } = useTranslations('onlineGiving', {
        givelifyButton: [
            'title',
            'needHelp',
            'preview',
            'lightGive',
            'darkGive',
            'primaryGive',
            'copyCode',
            'sharePage.preText',
            'sharePage.postText',
            'sharePage.link',
        ],
    });

    React.useEffect(() => {
        pushEvent(ONLINE_GIVING_LANDED);
    }, []);

    const [openShareModal, ShareModal] = useShareModal();
    const [openCustomModal, CustomModal] = useCustomModal();
    const [openInstructModal, InstructModal] = useInstructModal();

    const { onlineGivingURLs, routePaths } = useConnectorState();

    const givingLink =
        onlineGivingURLs.find((x) => x.channel === 'OGA')?.shortLink || '';

    return (
        <BasicPageWrapper
            showDivider
            contentWrapper="simple"
            title="Givelify button"
        >
            <ShareModal />
            <CustomModal />
            <InstructModal givingLink={givingLink} />
            <Breadcrumbs
                active="online-giving"
                hideBadge={true}
                options={[
                    {
                        id: 'online-giving',
                        label: 'Online giving',
                        onClick: () =>
                            routePaths.onlineGiving &&
                            navigate(routePaths.onlineGiving),
                    },
                    {
                        id: 'givelify-button',
                        label: 'Givelify button',
                    },
                ]}
            />
            <Container data-testid="givelify-button-content">
                <div>
                    <GivelifyLabel
                        data-testid="givelify-button-title"
                        text={gb.title}
                        variant="body1B"
                    />
                    <GivelifyLabel
                        data-testid="givelify-button-desc"
                        marginTop={1}
                        variant="body1"
                    >
                        <Trans
                            i18nKey="onlineGiving.givelifyButton.desc"
                            ns={I18N_NAMESPACE}
                        >
                            Copy and paste the code into your HTML code. If you
                            use a website builder like Wix or Squarespace
                            <GivelifyLink
                                data-testid="instruct-button"
                                onClick={openInstructModal}
                                text="click here"
                                variant="body1"
                            />
                            for instructions.
                        </Trans>
                    </GivelifyLabel>
                    <GivelifyLink
                        data-testid="givelify-help-link"
                        onClick={openCustomModal}
                        text={gb.needHelp}
                        variant="body1"
                    />
                </div>
                {GiveNowButtonTypes.map((type) => [
                    <SectionDivider key={`${type}-divider`} />,
                    <ButtonPreview
                        key={`${type}-preview`}
                        givingLink={givingLink}
                        type={type}
                    />,
                ])}
            </Container>
            <Stack marginBottom={6} marginTop={8} textAlign="center">
                <GivelifyLabel variant="body1">
                    {gb['sharePage.preText']}
                    <GivelifyLink
                        data-testid="givelify-share-modal-button"
                        onClick={openShareModal}
                        rel="noreferrer"
                        target="_blank"
                        text={gb['sharePage.link']}
                        variant="body1"
                    />
                    {gb['sharePage.postText']}
                </GivelifyLabel>
            </Stack>
        </BasicPageWrapper>
    );
};
